/**
 * The photograph, large, and close enough to argue with.
 *
 * The dialog around this could already show a picture. What it could not do
 * was answer the question the picture is opened for - *is this sharp, or did I
 * move?* - because a whole photograph scaled into a dialog is exactly the size
 * at which softness is invisible. Judging that means magnifying it.
 *
 * Two rules hold the honesty of this screen together.
 *
 * **Magnification stops where the picture's pixels stop.** One source pixel
 * per CSS pixel is what every photo viewer calls 100%, and this one will not
 * go past it. Beyond that the browser interpolates, and an interpolated pixel
 * is smooth - it reads as detail. A viewer that let you keep zooming would be
 * manufacturing the evidence you came here to check.
 *
 * **What is on screen is named.** The bytes are a bounded derivative, never
 * the upload, and the caption says at what resolution against the original.
 * A reader who has been told they are looking at 3200px of a 7680px photograph
 * can weigh their own verdict; one who has not will read the derivative's
 * smoothness as the photograph's.
 *
 * The larger derivative is fetched only when somebody actually magnifies, and
 * only when the one on screen is smaller than what they uploaded. Paging
 * through forty files still costs one bounded JPEG each.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { apiUrl } from "@/api/config";
import type { MediaAsset } from "@/api/types";
import { Button } from "@/components/ui";

/** One press of a zoom button, one wheel detent, one keystroke. */
const STEP = 1.6;

interface View {
  scale: number;
  x: number;
  y: number;
}

const FIT: View = { scale: 1, x: 0, y: 0 };

function previewUrl(asset: MediaAsset, size: "detail" | "inspect"): string {
  return apiUrl(`/media/${encodeURIComponent(asset.id)}/preview`, { size });
}

export function PreviewImage({ asset }: { asset: MediaAsset }) {
  const [failed, setFailed] = useState(false);
  const [inspecting, setInspecting] = useState(false);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [laidOut, setLaidOut] = useState<{ w: number; h: number } | null>(null);
  const [view, setView] = useState<View>(FIT);

  const stage = useRef<HTMLDivElement>(null);
  const image = useRef<HTMLImageElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ distance: number; midpoint: { x: number; y: number } } | null>(null);
  const upgrading = useRef(false);
  const upgradeRefused = useRef(false);

  // A new file is a new question. Carrying the last one's magnification over
  // is how somebody ends up calling a photograph soft while looking at the
  // corner of a different one.
  useEffect(() => {
    setFailed(false);
    setInspecting(false);
    setNatural(null);
    setLaidOut(null);
    setView(FIT);
    pointers.current.clear();
    pinch.current = null;
    upgrading.current = false;
    upgradeRefused.current = false;
  }, [asset.id]);

  const measure = useCallback(() => {
    const element = image.current;
    if (!element) return;
    // `offsetWidth` is the laid-out size. A CSS transform does not change it,
    // so this stays the fit size however far the reader has zoomed in.
    if (element.offsetWidth > 0) {
      setLaidOut({ w: element.offsetWidth, h: element.offsetHeight });
    }
    if (element.naturalWidth > 0) {
      setNatural({ w: element.naturalWidth, h: element.naturalHeight });
    }
  }, []);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  //: The furthest this picture may be magnified: the point at which one of its
  //: own pixels covers one CSS pixel. Never below 1, which is "the whole
  //: photograph, not enlarged" - a small file stays its own size rather than
  //: being blown up into a sharper-looking lie.
  const maxScale =
    natural && laidOut && laidOut.w > 0 ? Math.max(1, natural.w / laidOut.w) : 1;

  const clamp = useCallback(
    (next: View): View => {
      const scale = Math.min(Math.max(next.scale, 1), maxScale);
      const frame = stage.current;
      if (!laidOut || !frame) return { scale, x: 0, y: 0 };
      // How far the magnified picture overflows its frame, halved. Past this
      // the reader would be dragging the photograph off its own screen.
      const slackX = Math.max(0, (laidOut.w * scale - frame.clientWidth) / 2);
      const slackY = Math.max(0, (laidOut.h * scale - frame.clientHeight) / 2);
      return {
        scale,
        x: Math.min(Math.max(next.x, -slackX), slackX),
        y: Math.min(Math.max(next.y, -slackY), slackY),
      };
    },
    [laidOut, maxScale],
  );

  const upgrade = useCallback(() => {
    if (inspecting || upgrading.current || upgradeRefused.current) return;
    if (asset.media_type !== "photo" || !natural || !asset.width) return;
    // Already holding every pixel the upload has. A larger derivative of it
    // would be the same picture in a bigger file.
    if (natural.w >= asset.width) return;

    upgrading.current = true;
    const probe = new Image();
    // Swapped in only once it has decoded, so magnifying never blanks the
    // picture somebody is in the middle of looking at.
    probe.onload = () => {
      upgrading.current = false;
      setInspecting(true);
    };
    probe.onerror = () => {
      // The larger derivative failing is not the file failing. The picture
      // stays; only the extra magnification is lost, and the ceiling that
      // `maxScale` computes from the loaded image reflects that by itself.
      upgrading.current = false;
      upgradeRefused.current = true;
    };
    probe.src = previewUrl(asset, "inspect");
  }, [asset, inspecting, natural]);

  const anchorOf = useCallback((clientX: number, clientY: number) => {
    const rect = stage.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return { x: 0, y: 0 };
    return {
      x: clientX - (rect.left + rect.width / 2),
      y: clientY - (rect.top + rect.height / 2),
    };
  }, []);

  const zoomBy = useCallback(
    (factor: number, anchor: { x: number; y: number } = { x: 0, y: 0 }) => {
      setView((current) => {
        const scale = Math.min(Math.max(current.scale * factor, 1), maxScale);
        if (scale === current.scale) return current;
        // Keep whatever is under the cursor under the cursor. Zooming to the
        // middle of the frame instead is what makes a viewer feel like it is
        // fighting you: the detail you were looking at slides away.
        const ratio = scale / current.scale;
        return clamp({
          scale,
          x: anchor.x - (anchor.x - current.x) * ratio,
          y: anchor.y - (anchor.y - current.y) * ratio,
        });
      });
      if (factor > 1) upgrade();
    },
    [clamp, maxScale, upgrade],
  );

  useEffect(() => {
    const frame = stage.current;
    if (!frame) return;
    const onWheel = (event: WheelEvent) => {
      if (maxScale <= 1) return;
      // Without this the page scrolls behind the dialog while the picture
      // zooms: two things happening for one gesture.
      event.preventDefault();
      zoomBy(event.deltaY < 0 ? STEP : 1 / STEP, anchorOf(event.clientX, event.clientY));
    };
    // Registered here rather than as `onWheel`, because React's synthetic
    // wheel listener is passive and a passive listener may not preventDefault.
    frame.addEventListener("wheel", onWheel, { passive: false });
    return () => frame.removeEventListener("wheel", onWheel);
  }, [anchorOf, maxScale, zoomBy]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // The arrows belong to the dialog, which uses them to move through the
      // set. Only the magnification keys are claimed here.
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === "+" || event.key === "=") zoomBy(STEP);
      else if (event.key === "-" || event.key === "_") zoomBy(1 / STEP);
      else if (event.key === "0") setView(FIT);
      else return;
      event.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomBy]);

  const gestureOf = useCallback(() => {
    const [a, b] = [...pointers.current.values()];
    if (!a || !b) return null;
    return {
      distance: Math.hypot(a.x - b.x, a.y - b.y),
      midpoint: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
    };
  }, []);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 2) pinch.current = gestureOf();
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const previous = pointers.current.get(event.pointerId);
    if (!previous) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.current.size >= 2) {
      const before = pinch.current;
      const now = gestureOf();
      pinch.current = now;
      if (!before || !now || before.distance === 0) return;
      // Two fingers are one gesture: the spread magnifies and the shared
      // travel pans. Reading them as two gestures is what makes a pinch feel
      // like it is also trying to swipe to the next photograph.
      const ratio = now.distance / before.distance;
      const anchor = anchorOf(now.midpoint.x, now.midpoint.y);
      setView((current) => {
        const scale = Math.min(Math.max(current.scale * ratio, 1), maxScale);
        const step = scale / current.scale;
        return clamp({
          scale,
          x: anchor.x - (anchor.x - current.x) * step + (now.midpoint.x - before.midpoint.x),
          y: anchor.y - (anchor.y - current.y) * step + (now.midpoint.y - before.midpoint.y),
        });
      });
      if (ratio > 1) upgrade();
      return;
    }

    const dx = event.clientX - previous.x;
    const dy = event.clientY - previous.y;
    setView((current) =>
      // Dragging an un-magnified picture would move nothing and swallow the
      // click that closes the dialog.
      current.scale <= 1 ? current : clamp({ ...current, x: current.x + dx, y: current.y + dy }),
    );
  };

  const endPointer = (event: React.PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    pinch.current = pointers.current.size === 2 ? gestureOf() : null;
  };

  if (failed) {
    return (
      <div className="media-viewer__image media-viewer__image--missing">
        Bu dosya için önizleme oluşturulamadı.
      </div>
    );
  }

  const zoomed = view.scale > 1;
  const canZoom = maxScale > 1;
  const atCeiling = view.scale >= maxScale;
  // 100% means one file pixel on one screen pixel, which is where `maxScale`
  // sits. Anything else is a fraction of that, not of some invented ideal.
  const percent = Math.round((view.scale / maxScale) * 100);

  return (
    <figure className="media-viewer__figure">
      <div
        className={`media-zoom${zoomed ? " media-zoom--pannable" : ""}`}
        ref={stage}
        data-testid="media-zoom-stage"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onDoubleClick={(event) =>
          zoomed ? setView(FIT) : zoomBy(maxScale, anchorOf(event.clientX, event.clientY))
        }
      >
        <img
          ref={image}
          className="media-viewer__image"
          src={previewUrl(asset, inspecting ? "inspect" : "detail")}
          alt={`${asset.original_filename} önizlemesi`}
          decoding="async"
          draggable={false}
          style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
          onLoad={measure}
          onError={() => setFailed(true)}
        />
      </div>

      <div className="media-zoom__controls">
        <Button onClick={() => zoomBy(1 / STEP)} disabled={!zoomed} ariaLabel="Uzaklaştır">
          −
        </Button>
        <span className="faint media-zoom__level" role="status">
          %{percent}
        </span>
        <Button
          onClick={() => zoomBy(STEP)}
          disabled={!canZoom || atCeiling}
          ariaLabel="Yakınlaştır"
        >
          +
        </Button>
        <Button onClick={() => setView(FIT)} disabled={!zoomed}>
          Ekrana sığdır
        </Button>
        <Button
          onClick={() => zoomBy(maxScale)}
          disabled={!canZoom || atCeiling}
          title={
            canZoom
              ? "Dosyanın bir pikseli, ekranın bir pikseli"
              : "Bu görüntü zaten kendi boyutunda; büyütmek ayrıntı eklemez"
          }
        >
          %100
        </Button>
      </div>

      <figcaption className="faint">{caption(asset, natural)}</figcaption>
    </figure>
  );
}

/**
 * What is actually on screen, in pixels, against what was uploaded.
 *
 * The old caption said the picture was "küçültülmüş" and stopped there, which
 * was true and unusable: it gave a reader no way to know whether the softness
 * they were looking at belonged to their photograph or to the derivative. The
 * two numbers do.
 */
function caption(asset: MediaAsset, natural: { w: number; h: number } | null): string {
  if (asset.media_type === "video") {
    return "Videodan alınmış tek kare. Oynatma yapılmaz.";
  }
  if (!natural) {
    return "Küçültülmüş önizleme; yüklediğiniz dosyanın kendisi değildir.";
  }
  const shown = `${natural.w} × ${natural.h}`;
  if (!asset.width || !asset.height || natural.w >= asset.width) {
    return `Önizleme ${shown} piksel - dosyanızın tam çözünürlüğü, ancak dosyanın kendisi değil.`;
  }
  return (
    `Önizleme ${shown} piksel; yüklediğiniz dosya ${asset.width} × ${asset.height} piksel. ` +
    "Bu boyutta görünmeyen bir bulanıklık dosyada olabilir."
  );
}
